import { useReducer, useCallback } from "react";
import { GfFlyerItemDto } from "@swagger/typescript-fetch-goflyer";

// ============================================================================
// State Types
// ============================================================================

interface ViewportState {
  scale: number;
  position: { x: number; y: number };
}

interface ModalState {
  selection: boolean;
  saveConfirm: boolean;
}

interface EditorState {
  currentPage: number;
  selectedBoxIds: Set<string>;
  isDraggingBox: boolean;
  isSaving: boolean;
  groupMoveEnabled: boolean;
  multiSelectEnabled: boolean;
}

interface ItemsState {
  newItems: GfFlyerItemDto[];
  deletedItemIds: Set<string>;
}

export interface ViewerState {
  viewport: ViewportState;
  modals: ModalState;
  editor: EditorState;
  items: ItemsState;
}

// ============================================================================
// Action Types
// ============================================================================

type ViewerAction =
  // Viewport actions
  | { type: "SET_VIEWPORT_SCALE"; payload: number }
  | { type: "SET_VIEWPORT_POSITION"; payload: { x: number; y: number } }
  | {
      type: "SET_VIEWPORT";
      payload: { scale: number; position: { x: number; y: number } };
    }
  | { type: "RESET_VIEWPORT" }
  // Modal actions
  | { type: "OPEN_SELECTION_MODAL" }
  | { type: "CLOSE_SELECTION_MODAL" }
  | { type: "OPEN_SAVE_CONFIRM_MODAL" }
  | { type: "CLOSE_SAVE_CONFIRM_MODAL" }
  | { type: "CLOSE_ALL_MODALS" }
  // Editor actions
  | { type: "SET_CURRENT_PAGE"; payload: number }
  | { type: "TOGGLE_SELECTED_BOX_ID"; payload: string }
  | { type: "CLEAR_SELECTED_BOX_IDS" }
  | { type: "SELECT_SINGLE_BOX_ID"; payload: string }
  | { type: "SET_SELECTED_BOX_IDS"; payload: Set<string> }
  | { type: "SET_IS_DRAGGING_BOX"; payload: boolean }
  | { type: "SET_IS_SAVING"; payload: boolean }
  | { type: "SET_GROUP_MOVE_ENABLED"; payload: boolean }
  | { type: "SET_MULTI_SELECT_ENABLED"; payload: boolean }
  // Items actions
  | { type: "ADD_NEW_ITEM"; payload: GfFlyerItemDto }
  | { type: "REMOVE_NEW_ITEM"; payload: string }
  | { type: "CLEAR_NEW_ITEMS" }
  | { type: "DELETE_ITEM"; payload: string }
  | { type: "RESTORE_ITEM"; payload: string }
  | { type: "CLEAR_DELETED_ITEMS" };

// ============================================================================
// Initial State
// ============================================================================

const initialState: ViewerState = {
  viewport: {
    scale: 1,
    position: { x: 0, y: 0 },
  },
  modals: {
    selection: false,
    saveConfirm: false,
  },
  editor: {
    currentPage: 0,
    selectedBoxIds: new Set<string>(),
    isDraggingBox: false,
    isSaving: false,
    groupMoveEnabled: false,
    multiSelectEnabled: false,
  },
  items: {
    newItems: [],
    deletedItemIds: new Set(),
  },
};

// ============================================================================
// Reducer
// ============================================================================

function viewerReducer(state: ViewerState, action: ViewerAction): ViewerState {
  switch (action.type) {
    // Viewport actions
    case "SET_VIEWPORT_SCALE":
      return {
        ...state,
        viewport: { ...state.viewport, scale: action.payload },
      };
    case "SET_VIEWPORT_POSITION":
      return {
        ...state,
        viewport: { ...state.viewport, position: action.payload },
      };
    case "SET_VIEWPORT":
      return {
        ...state,
        viewport: action.payload,
      };
    case "RESET_VIEWPORT":
      return {
        ...state,
        viewport: { scale: 1, position: { x: 0, y: 0 } },
      };

    // Modal actions
    case "OPEN_SELECTION_MODAL":
      return {
        ...state,
        modals: { ...state.modals, selection: true },
      };
    case "CLOSE_SELECTION_MODAL":
      return {
        ...state,
        modals: { ...state.modals, selection: false },
      };
    case "OPEN_SAVE_CONFIRM_MODAL":
      return {
        ...state,
        modals: { ...state.modals, saveConfirm: true },
      };
    case "CLOSE_SAVE_CONFIRM_MODAL":
      return {
        ...state,
        modals: { ...state.modals, saveConfirm: false },
      };
    case "CLOSE_ALL_MODALS":
      return {
        ...state,
        modals: { selection: false, saveConfirm: false },
      };

    // Editor actions
    case "SET_CURRENT_PAGE":
      return {
        ...state,
        editor: { ...state.editor, currentPage: action.payload },
        // Don't reset viewport on page change - maintain current zoom/pan state
      };
    case "TOGGLE_SELECTED_BOX_ID": {
      const next = new Set(state.editor.selectedBoxIds);
      if (next.has(action.payload)) {
        next.delete(action.payload);
      } else {
        next.add(action.payload);
      }
      return { ...state, editor: { ...state.editor, selectedBoxIds: next } };
    }
    case "CLEAR_SELECTED_BOX_IDS":
      return {
        ...state,
        editor: { ...state.editor, selectedBoxIds: new Set<string>() },
      };
    case "SELECT_SINGLE_BOX_ID":
      return {
        ...state,
        editor: { ...state.editor, selectedBoxIds: new Set([action.payload]) },
      };
    case "SET_SELECTED_BOX_IDS":
      return {
        ...state,
        editor: { ...state.editor, selectedBoxIds: new Set(action.payload) },
      };
    case "SET_IS_DRAGGING_BOX":
      return {
        ...state,
        editor: { ...state.editor, isDraggingBox: action.payload },
      };
    case "SET_IS_SAVING":
      return {
        ...state,
        editor: { ...state.editor, isSaving: action.payload },
      };
    case "SET_GROUP_MOVE_ENABLED":
      return {
        ...state,
        editor: { ...state.editor, groupMoveEnabled: action.payload },
      };
    case "SET_MULTI_SELECT_ENABLED": {
      const enabled = action.payload;
      if (!enabled) {
        const firstId =
          state.editor.selectedBoxIds.size > 0
            ? state.editor.selectedBoxIds.values().next().value
            : undefined;
        return {
          ...state,
          editor: {
            ...state.editor,
            multiSelectEnabled: false,
            groupMoveEnabled: false,
            selectedBoxIds: firstId ? new Set([firstId]) : new Set<string>(),
          },
        };
      }
      return {
        ...state,
        editor: { ...state.editor, multiSelectEnabled: enabled },
      };
    }

    // Items actions
    case "ADD_NEW_ITEM":
      return {
        ...state,
        items: {
          ...state.items,
          newItems: [...state.items.newItems, action.payload],
        },
      };
    case "REMOVE_NEW_ITEM":
      return {
        ...state,
        items: {
          ...state.items,
          newItems: state.items.newItems.filter(
            (item) => item.id !== action.payload,
          ),
        },
      };
    case "CLEAR_NEW_ITEMS":
      return {
        ...state,
        items: { ...state.items, newItems: [] },
      };
    case "DELETE_ITEM": {
      const newDeletedIds = new Set(state.items.deletedItemIds);
      newDeletedIds.add(action.payload);
      return {
        ...state,
        items: { ...state.items, deletedItemIds: newDeletedIds },
      };
    }
    case "RESTORE_ITEM": {
      const newDeletedIds = new Set(state.items.deletedItemIds);
      newDeletedIds.delete(action.payload);
      return {
        ...state,
        items: { ...state.items, deletedItemIds: newDeletedIds },
      };
    }
    case "CLEAR_DELETED_ITEMS":
      return {
        ...state,
        items: { ...state.items, deletedItemIds: new Set() },
      };

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface UseViewerStateReturn {
  state: ViewerState;
  // Viewport actions
  setViewportScale: (scale: number) => void;
  setViewportPosition: (position: { x: number; y: number }) => void;
  setViewport: (viewport: {
    scale: number;
    position: { x: number; y: number };
  }) => void;
  resetViewport: () => void;
  // Modal actions
  openSelectionModal: () => void;
  closeSelectionModal: () => void;
  openSaveConfirmModal: () => void;
  closeSaveConfirmModal: () => void;
  closeAllModals: () => void;
  // Editor actions
  setCurrentPage: (page: number) => void;
  toggleSelectedBoxId: (id: string) => void;
  clearSelectedBoxIds: () => void;
  selectSingleBoxId: (id: string) => void;
  setSelectedBoxIds: (ids: Set<string>) => void;
  setIsDraggingBox: (dragging: boolean) => void;
  setIsSaving: (saving: boolean) => void;
  setGroupMoveEnabled: (enabled: boolean) => void;
  setMultiSelectEnabled: (enabled: boolean) => void;
  // Items actions
  addNewItem: (item: GfFlyerItemDto) => void;
  removeNewItem: (itemId: string) => void;
  clearNewItems: () => void;
  deleteItem: (itemId: string) => void;
  restoreItem: (itemId: string) => void;
  clearDeletedItems: () => void;
  // Computed values
  allFlyerItems: (originalItems: GfFlyerItemDto[]) => GfFlyerItemDto[];
  itemsWithCoordinates: (originalItems: GfFlyerItemDto[]) => Set<string>;
}

/**
 * Centralized state management hook for the FlyerCoordinateEditor component.
 *
 * This hook consolidates all viewer state into a single useReducer, replacing what was
 * previously 13 separate useState calls. It provides actions for managing viewport zoom/pan,
 * modal visibility, editor state, and item operations. Additionally, it exposes computed
 * values to eliminate derived state and prevent unnecessary re-renders.
 *
 * State categories managed:
 * - Viewport: zoom scale and pan position
 * - Modals: selection and save confirmation dialog states
 * - Editor: current page, selected box, drag state, save state
 * - Items: new items and deleted item tracking
 *
 * The hook provides memoized action dispatchers and computed values for optimal performance.
 *
 * @returns Object containing state, action dispatchers, and computed value functions
 */
export const useViewerState = (): UseViewerStateReturn => {
  const [state, dispatch] = useReducer(viewerReducer, initialState);

  const setViewportScale = useCallback((scale: number) => {
    dispatch({ type: "SET_VIEWPORT_SCALE", payload: scale });
  }, []);

  const setViewportPosition = useCallback(
    (position: { x: number; y: number }) => {
      dispatch({ type: "SET_VIEWPORT_POSITION", payload: position });
    },
    [],
  );

  const setViewport = useCallback(
    (viewport: { scale: number; position: { x: number; y: number } }) => {
      dispatch({ type: "SET_VIEWPORT", payload: viewport });
    },
    [],
  );

  const resetViewport = useCallback(() => {
    dispatch({ type: "RESET_VIEWPORT" });
  }, []);

  const openSelectionModal = useCallback(() => {
    dispatch({ type: "OPEN_SELECTION_MODAL" });
  }, []);

  const closeSelectionModal = useCallback(() => {
    dispatch({ type: "CLOSE_SELECTION_MODAL" });
  }, []);

  const openSaveConfirmModal = useCallback(() => {
    dispatch({ type: "OPEN_SAVE_CONFIRM_MODAL" });
  }, []);

  const closeSaveConfirmModal = useCallback(() => {
    dispatch({ type: "CLOSE_SAVE_CONFIRM_MODAL" });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: "CLOSE_ALL_MODALS" });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: page });
  }, []);

  const toggleSelectedBoxId = useCallback((id: string) => {
    dispatch({ type: "TOGGLE_SELECTED_BOX_ID", payload: id });
  }, []);

  const clearSelectedBoxIds = useCallback(() => {
    dispatch({ type: "CLEAR_SELECTED_BOX_IDS" });
  }, []);

  const selectSingleBoxId = useCallback((id: string) => {
    dispatch({ type: "SELECT_SINGLE_BOX_ID", payload: id });
  }, []);

  const setSelectedBoxIds = useCallback((ids: Set<string>) => {
    dispatch({ type: "SET_SELECTED_BOX_IDS", payload: ids });
  }, []);

  const setIsDraggingBox = useCallback((dragging: boolean) => {
    dispatch({ type: "SET_IS_DRAGGING_BOX", payload: dragging });
  }, []);

  const setIsSaving = useCallback((saving: boolean) => {
    dispatch({ type: "SET_IS_SAVING", payload: saving });
  }, []);

  const setGroupMoveEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: "SET_GROUP_MOVE_ENABLED", payload: enabled });
  }, []);

  const setMultiSelectEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: "SET_MULTI_SELECT_ENABLED", payload: enabled });
  }, []);

  const addNewItem = useCallback((item: GfFlyerItemDto) => {
    dispatch({ type: "ADD_NEW_ITEM", payload: item });
  }, []);

  const removeNewItem = useCallback((itemId: string) => {
    dispatch({ type: "REMOVE_NEW_ITEM", payload: itemId });
  }, []);

  const clearNewItems = useCallback(() => {
    dispatch({ type: "CLEAR_NEW_ITEMS" });
  }, []);

  const deleteItem = useCallback((itemId: string) => {
    dispatch({ type: "DELETE_ITEM", payload: itemId });
  }, []);

  const restoreItem = useCallback((itemId: string) => {
    dispatch({ type: "RESTORE_ITEM", payload: itemId });
  }, []);

  const clearDeletedItems = useCallback(() => {
    dispatch({ type: "CLEAR_DELETED_ITEMS" });
  }, []);

  /**
   * Computes the complete list of flyer items including both original and newly added items.
   *
   * Merges the original items array with any new items that have been created during the
   * current session. This eliminates the need for maintaining separate derived state.
   *
   * @param originalItems - The original array of flyer items from props
   * @returns Combined array of original and new items
   */
  const allFlyerItems = useCallback(
    (originalItems: GfFlyerItemDto[]) => {
      return [...originalItems, ...state.items.newItems];
    },
    [state.items.newItems],
  );

  /**
   * Computes a set of item IDs that have coordinates assigned to flyer pages.
   *
   * Filters through all items (original and new) to identify which ones have valid
   * coordinates on flyer pages. Excludes items that have been marked for deletion.
   * This eliminates the need for separate useState and useEffect to track this data.
   *
   * @param originalItems - The original array of flyer items from props
   * @returns Set of item IDs that have coordinates and are not deleted
   */
  const itemsWithCoordinates = useCallback(
    (originalItems: GfFlyerItemDto[]): Set<string> => {
      const withCoords = new Set<string>();
      const allItems = [...originalItems, ...state.items.newItems];

      allItems.forEach((item) => {
        if (
          item.coordinate &&
          item.coordinate.flyerPage &&
          !state.items.deletedItemIds.has(item.id!)
        ) {
          withCoords.add(item.id!);
        }
      });

      return withCoords;
    },
    [state.items.newItems, state.items.deletedItemIds],
  );

  return {
    state,
    // Viewport
    setViewportScale,
    setViewportPosition,
    setViewport,
    resetViewport,
    // Modals
    openSelectionModal,
    closeSelectionModal,
    openSaveConfirmModal,
    closeSaveConfirmModal,
    closeAllModals,
    // Editor
    setCurrentPage,
    toggleSelectedBoxId,
    clearSelectedBoxIds,
    selectSingleBoxId,
    setSelectedBoxIds,
    setIsDraggingBox,
    setIsSaving,
    setGroupMoveEnabled,
    setMultiSelectEnabled,
    // Items
    addNewItem,
    removeNewItem,
    clearNewItems,
    deleteItem,
    restoreItem,
    clearDeletedItems,
    // Computed
    allFlyerItems,
    itemsWithCoordinates,
  };
};
